import { onValue, ref } from 'firebase/database';
import { database } from './firebase';
import { nativeGet, nativeOnValue, shouldUseNativeDatabase } from './firebase-native-database';

const accessPath = (teamUid: string) => `teams/${teamUid}/control/uuid`;
const isPermissionDenied = (error: unknown): boolean =>
  error instanceof Error && /permission.?denied|permission denied|401|403/i.test(error.message);

export const hasTeamAccess = async (teamUid: string): Promise<boolean> => {
  try {
    // REST avoids the web SDK's offline cached-read fallback for permission checks.
    await nativeGet(accessPath(teamUid), { throwOnError: true });
    return true;
  } catch (error) {
    if (!isPermissionDenied(error)) throw error;
    console.warn('Team access denied:', teamUid);
    return false;
  }
};

export const watchSystemAdmin = (uid: string, onAdmin: (admin: boolean) => void): (() => void) => {
  const path = `systemAdmins/${uid}`;
  if (shouldUseNativeDatabase()) {
    return nativeOnValue(path, snapshot => onAdmin(snapshot.val() === true));
  }
  return onValue(ref(database, path), snapshot => onAdmin(snapshot.val() === true), error => {
    console.error('Unable to verify system administrator permissions:', error);
    onAdmin(false);
  });
};

export const watchTeamAccess = (teamUid: string, onAccess: (allowed: boolean) => void): (() => void) => {
  if (!shouldUseNativeDatabase()) {
    return onValue(ref(database, accessPath(teamUid)), () => onAccess(true), error => {
      console.error('Team access check failed:', error);
      onAccess(false);
    });
  }
  let active = true;
  let checking = false;
  const check = async () => {
    if (checking) return;
    checking = true;
    try {
      const allowed = await hasTeamAccess(teamUid);
      if (active) onAccess(allowed);
    } catch (error) {
      console.error('Team access check failed:', error);
      if (active) onAccess(false);
    } finally {
      checking = false;
    }
  };
  void check();
  const interval = setInterval(check, 5000);
  return () => {
    active = false;
    clearInterval(interval);
  };
};
